import React, { useState } from 'react';
import {
  PencilSimple,
  Trash,
  CaretDown,
  CaretRight,
  ClipboardText,
  Question,
  CheckCircle,
  XCircle,
  Building,
  House,
  Robot,
  // All category icons - same as DueDiligenceEntityCard
  HouseLine,
  HouseSimple,
  Buildings,
  BuildingOffice,
  Warehouse,
  Storefront,
  Bank,
  Factory,
  Hospital,
  Church,
  Garage,
  Door,
  Stairs,
  Elevator,
  Blueprint,
  MapTrifold,
  MapPin,
  Compass,
  Path,
  Fire,
  Lightning,
  Plug,
  Plugs,
  Drop,
  Pipe,
  Snowflake,
  Thermometer,
  Fan,
  Sun,
  SolarPanel,
  Wind,
  Lightbulb,
  Lamp,
  Broadcast,
  WifiHigh,
  ShieldCheck,
  ShieldWarning,
  Shield,
  Lock,
  LockOpen,
  Key,
  Eye,
  Warning,
  Skull,
  Siren,
  FirstAid,
  Fingerprint,
  VideoCamera,
  Wrench,
  Hammer,
  Screwdriver,
  Toolbox,
  Gear,
  Faders,
  PaintRoller,
  Broom,
  Leaf,
  Tree,
  Plant,
  Flower,
  Recycle,
  Globe,
  Mountains,
  Waves,
  CloudRain,
  CloudSun,
  Umbrella,
  Armchair,
  Bed,
  Bathtub,
  Toilet,
  Shower,
  CookingPot,
  Ruler,
  Stack,
  Certificate,
  FileText,
  Notebook,
  Calculator,
  ChartLine,
  ChartBar,
  Scales,
  Car,
  Truck,
  Bicycle,
  Train,
  Airplane,
  Boat,
  Users,
  User,
  Handshake,
  Phone,
  Envelope,
  ChatCircle,
  Bell,
  Megaphone,
  Clock,
  Calendar,
  Hourglass,
  Timer,
  Heartbeat,
  Heart,
  Pill,
  Stethoscope,
  Syringe,
  Bandaids,
  Atom,
  Flask,
  TestTube,
  Microscope,
  GraduationCap,
  BookOpen,
  Money,
  CurrencyEur,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  TrendUp,
  Percent,
  Camera,
  MusicNote,
  Headphones,
  Television,
  GameController,
  Diamond,
  Star,
  Flag,
  Trophy,
  Target,
  Cube,
  TreeStructure,
  PuzzlePiece,
  Tag,
  Bookmark,
  Archive,
  Package,
  Gift,
  Barcode,
  QrCode,
  Check,
  X,
  Info,
  IconProps,
} from '@phosphor-icons/react';
import {
  DueDiligenceCategory,
  DueDiligenceScope,
  DueDiligenceEvaluationMode,
  statusColorToClasses,
} from 'shared/types/due-diligence';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

// Icon map for category icons - comprehensive set matching settings page
const CATEGORY_ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  House,
  HouseLine,
  HouseSimple,
  Building,
  Buildings,
  BuildingOffice,
  Warehouse,
  Storefront,
  Bank,
  Factory,
  Hospital,
  Church,
  Garage,
  Door,
  Stairs,
  Elevator,
  Blueprint,
  MapTrifold,
  MapPin,
  Compass,
  Path,
  Fire,
  Lightning,
  Plug,
  Plugs,
  Drop,
  Pipe,
  Snowflake,
  Thermometer,
  Fan,
  Sun,
  SolarPanel,
  Wind,
  Lightbulb,
  Lamp,
  Broadcast,
  WifiHigh,
  ShieldCheck,
  ShieldWarning,
  Shield,
  Lock,
  LockOpen,
  Key,
  Eye,
  Warning,
  Skull,
  Siren,
  FirstAid,
  Fingerprint,
  VideoCamera,
  Wrench,
  Hammer,
  Screwdriver,
  Toolbox,
  Gear,
  Faders,
  PaintRoller,
  Broom,
  Leaf,
  Tree,
  Plant,
  Flower,
  Recycle,
  Globe,
  Mountains,
  Waves,
  CloudRain,
  CloudSun,
  Umbrella,
  Armchair,
  Bed,
  Bathtub,
  Toilet,
  Shower,
  CookingPot,
  Ruler,
  Stack,
  Certificate,
  FileText,
  Notebook,
  Calculator,
  ChartLine,
  ChartBar,
  Scales,
  ClipboardText,
  Car,
  Truck,
  Bicycle,
  Train,
  Airplane,
  Boat,
  Users,
  User,
  Handshake,
  Phone,
  Envelope,
  ChatCircle,
  Bell,
  Megaphone,
  Clock,
  Calendar,
  Hourglass,
  Timer,
  Heartbeat,
  Heart,
  Pill,
  Stethoscope,
  Syringe,
  Bandaids,
  Atom,
  Flask,
  TestTube,
  Microscope,
  GraduationCap,
  BookOpen,
  Money,
  CurrencyEur,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  TrendUp,
  Percent,
  Camera,
  MusicNote,
  Headphones,
  Television,
  GameController,
  Diamond,
  Star,
  Flag,
  Trophy,
  Target,
  Cube,
  TreeStructure,
  PuzzlePiece,
  Tag,
  Bookmark,
  Archive,
  Package,
  Gift,
  Barcode,
  QrCode,
  Check,
  X,
  Info,
};

/**
 * Get the icon component for a category
 */
const getCategoryIcon = (iconName?: string): React.ComponentType<IconProps> => {
  if (iconName && CATEGORY_ICON_MAP[iconName]) {
    return CATEGORY_ICON_MAP[iconName];
  }
  return ClipboardText; // Default icon
};

interface DueDiligenceCategoryListProps {
  categories: DueDiligenceCategory[];
  onEdit: (category: DueDiligenceCategory) => void;
  onDeactivate: (category: DueDiligenceCategory) => void;
  isLoading?: boolean;
}

export const DueDiligenceCategoryList: React.FC<DueDiligenceCategoryListProps> = ({
  categories,
  onEdit,
  onDeactivate,
  isLoading = false,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const toggleExpanded = (categoryId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const sortedCategories = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

  const getScopeLabel = (scope: DueDiligenceScope) => {
    return scope === DueDiligenceScope.BUILDING ? 'Gebouw' : 'Unit';
  };

  const getScopeIcon = (scope: DueDiligenceScope) => {
    return scope === DueDiligenceScope.BUILDING ? (
      <Building className="h-3 w-3" />
    ) : (
      <House className="h-3 w-3" />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-8 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            <ClipboardText className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-lg mb-2">Nog geen categorieën</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Maak je eerste due diligence categorie aan om te beginnen met het configureren
            van controle vragen voor je vastgoedportefeuille.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {sortedCategories.map((category) => {
        const isExpanded = expandedIds.has(category.id);
        const hasQuestions = (category.questions?.length || 0) > 0;

        return (
          <Card key={category.id} className={!category.active ? 'opacity-60' : ''}>
            <CardContent className="p-0">
              {/* Main Row */}
              <div className="flex items-center gap-4 p-4">
                {/* Expand trigger */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 shrink-0 ${!hasQuestions ? 'invisible' : ''}`}
                  onClick={() => hasQuestions && toggleExpanded(category.id)}
                >
                  {isExpanded ? (
                    <CaretDown className="h-4 w-4" />
                  ) : (
                    <CaretRight className="h-4 w-4" />
                  )}
                </Button>

                {/* Category icon */}
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  {React.createElement(getCategoryIcon(category.icon), {
                    weight: 'duotone',
                    className: 'h-5 w-5 text-primary',
                  })}
                </div>

                {/* Category info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{category.name}</h4>
                    <Badge variant="outline" className="flex items-center gap-1">
                      {getScopeIcon(category.scope)}
                      {getScopeLabel(category.scope)}
                    </Badge>
                    {category.active ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Actief
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inactief
                      </Badge>
                    )}
                  </div>
                  {category.description && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">
                      {category.description}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 shrink-0">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Question className="h-4 w-4" />
                    <span className="font-medium">{category.questions?.length || 0}</span>
                    <span className="hidden sm:inline">vragen</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(category);
                    }}
                    title="Bewerken"
                  >
                    <PencilSimple className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeactivate(category);
                    }}
                    title={category.active ? 'Deactiveren' : 'Activeren'}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded Content - Questions */}
              {isExpanded && hasQuestions && (
                <div className="px-4 pb-4 pt-0">
                  <div className="pl-12 border-t pt-4">
                    <h5 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <Question className="h-4 w-4" />
                      Vragen ({category.questions?.length || 0})
                    </h5>
                    <div className="space-y-2">
                      {category.questions?.map((question) => (
                        <div
                          key={question.id}
                          className="p-3 bg-muted/50 rounded-md"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <span className="text-sm font-medium">{question.displayName}</span>
                              {question.helpText && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {question.helpText}
                                </p>
                              )}
                            </div>
                            {question.evaluationMode === DueDiligenceEvaluationMode.FILE_BASED ? (
                              <Badge variant="secondary" className="text-xs ml-2 bg-blue-50 text-blue-700 border-blue-200">
                                <Robot className="h-3 w-3 mr-1" />
                                Automatisch
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs ml-2">
                                {question.answerOptions?.length || 0} opties
                              </Badge>
                            )}
                          </div>
                          {/* Answer options preview */}
                          {question.answerOptions && question.answerOptions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {question.answerOptions.map((option) => (
                                <div
                                  key={option.id}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-white rounded border text-xs"
                                >
                                  <span
                                    className={`w-2.5 h-2.5 rounded-full ${statusColorToClasses[option.statusColor]}`}
                                  />
                                  <span>{option.label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
